import { setupListeners } from "@reduxjs/toolkit/query";
import {
    configureStore,
    ThunkAction,
    Action,
    combineReducers,
} from "@reduxjs/toolkit";

import { coreSlice } from "features/core/store/slice";
import { sitesSlice } from "features/sites/store/slice";
import { devicesSlice } from "features/devices/store/slice";
import { liveStreamingSlice } from "features/liveStreaming/store/slice";

import { playbackSlice } from "features/playback/store/slice";
import { messagesSlice } from "features/messages/store/slice";
import { listenerMiddleware } from "./listenerMiddleware";

import { configApi } from "services/config.api";
import { streamingApi } from "services/streamingApi/base.api";
import { graphApi } from "services/graph.api";
import { appFeaturesSlice } from "features/appFeatures/store/slice";
import { casesReducer } from "features/cases/store/slice";
import { userSlice } from "features/user/store/slice";
import { diagnosticsSlice } from "features/diagnostics/store/slice";
import { reIdSlice } from "features/cases/caseViewer/reId/store/slice";
const devToolsEnabledEnvironmentNames = ["development", "test", "staging"];

const rootReducer = combineReducers({
    // features
    [coreSlice.name]: coreSlice.reducer,
    [sitesSlice.name]: sitesSlice.reducer,
    [devicesSlice.name]: devicesSlice.reducer,
    [liveStreamingSlice.name]: liveStreamingSlice.reducer,
    ["cases"]: casesReducer,
    [playbackSlice.name]: playbackSlice.reducer,
    [messagesSlice.name]: messagesSlice.reducer,
    [appFeaturesSlice.name]: appFeaturesSlice.reducer,
    [userSlice.name]: userSlice.reducer,
    [diagnosticsSlice.name]: diagnosticsSlice.reducer,
    [reIdSlice.name]: reIdSlice.reducer,

    // services
    [configApi.reducerPath]: configApi.reducer,
    [streamingApi.reducerPath]: streamingApi.reducer,
    [graphApi.reducerPath]: graphApi.reducer,
});

export const setupStore = (preloadedState?: Partial<RootState>) => {
    const store = configureStore({
        reducer: rootReducer,

        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware()
                .concat(listenerMiddleware.middleware)
                .concat(configApi.middleware)
                .concat(streamingApi.middleware)
                .concat(graphApi.middleware),

        devTools: devToolsEnabledEnvironmentNames.some(
            (envName) =>
                envName.toLowerCase() ===
                import.meta.env.VITE_ENV_NAME?.toLowerCase(),
        ),

        preloadedState,
    });

    // optional, but required for refetchOnFocus/refetchOnReconnect behaviors
    // see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
    setupListeners(store.dispatch);

    return store;
};

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
export type RootState = ReturnType<typeof rootReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<
    ReturnType,
    RootState,
    unknown,
    Action<string>
>;
